import type { IconGlyph } from '@sunwebgroup/ui-components'
import type { ContentUsps } from '@/scopes/prices/infrastructure/transport/types'

export interface PaymentMethod {
  icon: string
  text: string
}
export interface BusSelectorMappedResponse {
  transportType: string
  outboundBuses: BusInfo[]
  inboundBuses: BusInfo[]
  routes: Record<string, Record<string, BusRouteInfo>>
  tripSummary: TripSummary
  paymentMethod: PaymentMethod | null
}

export interface BusRouteInfo {
  outboundBusId: string
  inboundBusId: string
  price: number
  priceBreakdown: PriceBreakdown
  tripSummary: TripSummary
}

export interface BusInfo {
  id: string
  departureDate: RouteDate
  arrivalDate: RouteDate
  departureCityName: string
  arrivalCityName: string
  departureCode: string
  departureStationName: string
  arrivalCode: string
  arrivalStationName: string
  averagePrice: number
  totalPrice: number
}

export interface TransportSelectorMappedResponse {
  transportType: string
  outboundRoutes: RouteInfo[]
  inboundRoutes: RouteInfo[]
  tripSummary: TripSummary
  paymentMethod: PaymentMethod | null
}

export interface RouteInfo {
  id: string
  airline: Airline | null
  arrivalDate: RouteDate
  departureDate: RouteDate
  flightType: 'flexible' | 'standard' | null
  luggage: Luggage[] | null
  averagePrice: number
  totalPrice: number
  route: Route
  stationName: string | null
  relatedRoutes?: RelatedInboundRoute[]
  remark?: string
  isDefault?: boolean
  flightDuration?: number | null
  stopovers: string[]
}

export enum LuggageType {
  Hold = 'hold',
  Hand = 'hand',
}

export interface Luggage {
  type: LuggageType
  weight: number
}

export interface Route {
  from: {
    code: string
    name: string
  }
  to: {
    code: string
    name: string
  }
}

export interface RelatedInboundRoute extends RouteInfo {
  tripSummary: TripSummary
  priceBreakdown: PriceBreakdown
}

export interface RouteDate {
  time: string
  fullDate: string
  rawDate: string
}

interface Airline {
  id: string
  name: string
}

export interface SelfDriveSelectorResponse {
  transportType: string
  tripSummary?: TripSummary
  priceBreakdown?: PriceBreakdown
  contentUsps?: ContentUsps
  paymentMethod: PaymentMethod | null
}

export interface TripSummaryItem {
  header: {
    icon: IconGlyph
    text: string
    stars?: number
  }
  extraInfo: string[]
}

export type TripSummary = TripSummaryItem[]

export interface PriceBreakdown {
  atReservation: {
    rooms: PriceItem[][]
    services: ServiceItem[]
    subtotal: number
  }
  atDestination: {
    items: ServiceItem[]
    subtotal: number
  }
  totalPrice: number
  totalGrossPrice: number
  pricePerPerson: number
  extraCostsText: {
    title: string
    text: string
  }
  usps: Usp[]
  countDown?: {
    icon: IconGlyph
    deadline: string
    text: string
  }
}

interface Usp {
  type: string
  title: string
  text?: string
}

interface PriceItem {
  price: number
  valueText: string
  description: string
  isDiscount: boolean
}

export interface ServiceItem extends PriceItem {
  header: string
}

export enum FlightType {
  Inbound,
  Outbound,
}

export enum BusType {
  Inbound,
  Outbound,
}
